import { Badge } from '@/components/ui/badge';
import { Zap, AlertTriangle, Clock, Circle } from 'lucide-react';

interface PriorityIndicatorProps {
  priority: number; // 1-5 scale
  size?: 'xs' | 'sm' | 'md';
  showText?: boolean;
  variant?: 'dot' | 'badge' | 'icon';
}

export function PriorityIndicator({ 
  priority, 
  size = 'sm', 
  showText = false, 
  variant = 'badge' 
}: PriorityIndicatorProps) {
  const getPriorityConfig = (priority: number) => {
    if (priority >= 5) {
      return {
        label: 'Critique',
        color: 'bg-red-500',
        textColor: 'text-red-700',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        icon: AlertTriangle,
        pulse: true
      };
    } else if (priority >= 4) {
      return {
        label: 'Urgent',
        color: 'bg-orange-500',
        textColor: 'text-orange-700',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        icon: Zap,
        pulse: false
      };
    } else if (priority >= 3) {
      return {
        label: 'Important',
        color: 'bg-yellow-500',
        textColor: 'text-yellow-700',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        icon: Clock,
        pulse: false
      };
    } else if (priority >= 2) {
      return {
        label: 'Normal',
        color: 'bg-blue-500',
        textColor: 'text-blue-700',
        bgColor: 'bg-blue-50',
        borderColor: 'border-blue-200',
        icon: Circle,
        pulse: false
      };
    } else {
      return {
        label: 'Faible',
        color: 'bg-slate-400',
        textColor: 'text-slate-600',
        bgColor: 'bg-slate-50',
        borderColor: 'border-slate-200',
        icon: Circle,
        pulse: false
      };
    }
  };

  const config = getPriorityConfig(priority);
  const sizeClasses = {
    xs: { dot: 'w-1.5 h-1.5', icon: 'h-2 w-2', text: 'text-xs' },
    sm: { dot: 'w-2 h-2', icon: 'h-3 w-3', text: 'text-xs' },
    md: { dot: 'w-3 h-3', icon: 'h-4 w-4', text: 'text-sm' }
  };

  if (priority <= 1) {
    return null; // Don't show indicator for low priority items
  }

  if (variant === 'dot') {
    return (
      <div 
        className={`rounded-full ${config.color} ${sizeClasses[size].dot} ${
          config.pulse ? 'animate-pulse' : ''
        }`}
        title={config.label}
      />
    );
  }

  if (variant === 'icon') {
    const IconComponent = config.icon;
    return (
      <div 
        className={`${config.textColor} ${config.pulse ? 'animate-pulse' : ''}`}
        title={config.label}
      >
        <IconComponent className={sizeClasses[size].icon} />
      </div>
    );
  }

  // Default badge variant - removed
  return null;
}